import heic2any from 'heic2any'
import { uploadToS3 } from './s3Utils'

// Detect HEIC format
const isHEICFormat = (file) => {
  return (
    file.type === 'image/heic' ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  )
}

// Convert HEIC to PNG
const convertHEICToPNG = async (file) => {
  try {
    // Use heic2any to convert HEIC to PNG blob
    const pngBlob = await heic2any({
      blob: file,
      toType: 'image/png',
      quality: 0.8,
    })

    // If the result is an array (multiple images), take the first one
    const resultBlob = Array.isArray(pngBlob) ? pngBlob[0] : pngBlob

    // Create a new File object with PNG extension
    const fileName = file.name.replace(/\.(heic|heif)$/i, '.png')
    return new File([resultBlob], fileName, { type: 'image/png' })
  } catch (error) {
    console.error('HEIC conversion error:', error)
    throw new Error('Failed to convert HEIC/HEIF image. ' + error.message)
  }
}

// Compress image if needed
const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = URL.createObjectURL(file)

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img
      const MAX_WIDTH = 1920
      const MAX_HEIGHT = 1080

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width)
          width = MAX_WIDTH
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height)
          height = MAX_HEIGHT
        }
      }

      canvas.width = width
      canvas.height = height

      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'))
            return
          }
          resolve(new File([blob], file.name, { type: 'image/jpeg' }))
        },
        'image/jpeg',
        0.8
      )
    }

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'))
    }
  })
}

// Upload image to S3
export const uploadImageToS3 = async (file, userEmail) => {
  try {
    // Validate file
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image.')
    }

    // Convert HEIC/HEIF files to PNG instead of blocking them
    let processedFile = file
    if (isHEICFormat(file)) {
      try {
        processedFile = await convertHEICToPNG(file)
      } catch (error) {
        throw new Error(`HEIC/HEIF conversion failed: ${error.message}`)
      }
    }

    // Compress image if it's not a GIF
    if (!processedFile.type.includes('gif')) {
      try {
        processedFile = await compressImage(processedFile)
      } catch (error) {
        console.warn('Image compression failed, using original file:', error)
      }
    }

    try {
      // Use the uploadToS3 function from s3Utils.js
      return await uploadToS3(processedFile, userEmail)
    } catch (error) {
      console.error('Error uploading to S3:', error)
      throw new Error(`Upload failed: ${error.message}`)
    }
  } catch (error) {
    console.error('Error uploading image:', error)
    throw new Error(error.message || 'Failed to upload image')
  }
}

// Handle image capture from camera
export const captureImage = async (videoElement) => {
  try {
    if (!videoElement || !videoElement.videoWidth) {
      throw new Error('Invalid video element or stream not ready')
    }

    const canvas = document.createElement('canvas')
    canvas.width = videoElement.videoWidth
    canvas.height = videoElement.videoHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Could not get canvas context')
    }

    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to capture image'))
            return
          }
          const file = new File([blob], 'camera-capture.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })
          resolve(file)
        },
        'image/jpeg',
        0.8
      )
    })
  } catch (error) {
    console.error('Error capturing image:', error)
    throw new Error(error.message || 'Failed to capture image')
  }
}
